import { useNavigate } from 'react-router-dom'
import { Lock, Zap, Star, ArrowRight, CheckCircle } from 'lucide-react'

/**
 * Full-screen paywall shown when the free trial (or paid subscription) has expired.
 * Rendered by DashboardLayout — blocks access to all dashboard pages.
 */
export default function TrialExpiredPaywall({ isTrialExpired = true }) {
  const navigate = useNavigate()

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-50 dark:bg-background-dark px-4">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 dot-pattern opacity-30 pointer-events-none" />

      <div className="relative w-full max-w-2xl">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 rounded-2xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center shadow-lg">
            <Lock className="w-8 h-8 text-amber-500" />
          </div>
        </div>

        {/* Headline */}
        <div className="text-center mb-8">
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3">
            {isTrialExpired ? 'Your free trial has ended' : 'Subscription required'}
          </h1>
          <p className="text-base sm:text-lg text-gray-500 dark:text-gray-400 max-w-md mx-auto">
            {isTrialExpired
              ? 'You\'ve completed your 14-day free trial. Choose a plan to keep your projects and data.'
              : 'Your subscription has expired. Renew to restore access to your workspace.'}
          </p>
        </div>

        {/* Plan cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          {/* Start */}
          <div className="bg-white dark:bg-surface-dark rounded-2xl border border-gray-200 dark:border-gray-700 p-6 hover:border-accent/50 transition-all">
            <div className="flex items-center gap-2 mb-3">
              <Star className="w-5 h-5 text-accent" />
              <span className="font-bold text-gray-900 dark:text-white">Start Plan</span>
            </div>
            <div className="mb-4">
              <span className="text-3xl font-black text-gray-900 dark:text-white">$9</span>
              <span className="text-sm text-gray-500">/mo</span>
            </div>
            <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400 mb-5">
              {['5 active projects', '10 GB storage', 'Client review links', 'Email support'].map(f => (
                <li key={f} className="flex items-center gap-2">
                  <CheckCircle className="w-3.5 h-3.5 text-accent shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
            <button
              onClick={() => navigate('/subscriptions')}
              className="w-full py-2.5 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white font-semibold text-sm hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              Choose Start
            </button>
          </div>

          {/* Pro */}
          <div className="bg-white dark:bg-surface-dark rounded-2xl border-2 border-accent p-6 relative shadow-lg shadow-accent/10">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="bg-accent text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-widest whitespace-nowrap">
                Most Popular
              </span>
            </div>
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-5 h-5 text-accent" />
              <span className="font-bold text-accent">Pro Plan</span>
            </div>
            <div className="mb-4">
              <span className="text-3xl font-black text-gray-900 dark:text-white">$15</span>
              <span className="text-sm text-gray-500">/mo</span>
            </div>
            <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400 mb-5">
              {['Unlimited projects', '100 GB storage', 'Custom branding', 'Priority support', 'Advanced analytics'].map(f => (
                <li key={f} className="flex items-center gap-2">
                  <CheckCircle className="w-3.5 h-3.5 text-accent shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
            <button
              onClick={() => navigate('/subscriptions')}
              className="w-full py-2.5 rounded-xl bg-accent text-white font-semibold text-sm hover:opacity-90 transition-opacity shadow-md shadow-accent/20 flex items-center justify-center gap-2"
            >
              Upgrade to Pro <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Footer note */}
        <p className="text-center text-xs text-gray-400 dark:text-gray-500">
          Your data is safe and waiting for you. Upgrade any time to restore full access.
          <br />
          Questions?{' '}
          <a href="mailto:support@proofflow.io" className="text-accent hover:underline">
            Contact support
          </a>
        </p>
      </div>
    </div>
  )
}
